import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { ArrowRight, Sparkles, Check, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhiteLabelOnboarding() {
  const { user, token } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    admin_email: user?.email || "",
    contact_phone: "",
    primary_color: "#2E4FB8",
    logo_url: "",
    preferred_subdomain: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pre-fill from existing order (if buyer comes back)
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/whitelabel-orders/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data?.id) {
          setForm((f) => ({
            ...f,
            business_name: r.data.business_name || f.business_name,
            admin_email: r.data.admin_email || f.admin_email,
            contact_phone: r.data.contact_phone || f.contact_phone,
            primary_color: r.data.primary_color || f.primary_color,
            logo_url: r.data.logo_url || f.logo_url,
            preferred_subdomain: r.data.preferred_subdomain || f.preferred_subdomain,
            notes: r.data.notes || f.notes,
          }));
          if (r.data.status === "delivered") setSubmitted(r.data);
        }
      })
      .catch(() => {});
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.admin_email.trim()) {
      toast.error("Business name and admin email are required.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/whitelabel-orders`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmitted(r.data);
      toast.success("Onboarding submitted — we'll email you when your instance is ready.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="max-w-md text-center border border-border rounded-lg p-8 bg-card" data-testid="whitelabel-onboarding-signin">
          <h1 className="font-serif text-3xl mb-3">Sign in to continue setup</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Your $699 purchase is safe — sign in with the email you used at checkout to finish onboarding.
          </p>
          <Button asChild size="lg">
            <Link to="/login?next=/white-label/onboarding">Sign in <ArrowRight size={14} className="ml-2" /></Link>
          </Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    const p = submitted.provision;
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto p-6 lg:p-12 py-16" data-testid="whitelabel-onboarding-success">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 text-primary mb-6">
              <Check size={26} />
            </div>
            <div className="label-caps mb-2 text-primary">Your instance is live</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-3">
              Welcome to <span className="italic text-primary">{submitted.business_name}.</span>
            </h1>
            <p className="text-muted-foreground">
              We've spun up your branded platform with your logo, colors, and business name. Bookmark your URL below — this is your new office.
            </p>
          </div>

          {p ? (
            <div className="border-2 border-primary rounded-lg p-6 bg-primary/5 mb-6" data-testid="whitelabel-provision-card">
              <div className="label-caps text-[10px] mb-4 text-primary">Your login details</div>
              <div className="space-y-4">
                <div>
                  <div className="label-caps text-[10px] mb-1">Your branded site</div>
                  <a href={p.branded_url} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary underline break-all" data-testid="whitelabel-branded-url">
                    {p.branded_url}
                  </a>
                </div>
                <div>
                  <div className="label-caps text-[10px] mb-1">Admin login URL</div>
                  <a href={p.login_url} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary underline break-all">
                    {p.login_url}
                  </a>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-primary/30">
                  <div>
                    <div className="label-caps text-[10px] mb-1">Email</div>
                    <code className="text-sm break-all">{p.admin_email}</code>
                  </div>
                  <div>
                    <div className="label-caps text-[10px] mb-1">Temporary password</div>
                    <code className="text-sm">{p.admin_temp_password || "Use your existing password"}</code>
                  </div>
                </div>
                {p.admin_temp_password && (
                  <div className="text-xs text-muted-foreground border-t border-primary/30 pt-3">
                    <strong className="text-foreground">Important:</strong> Save this password before leaving this page — you'll use it to log in for the first time.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="border border-border rounded-lg p-6 bg-card mb-6 text-sm text-muted-foreground">
              Your onboarding info is saved. If your branded instance doesn't appear within 2 minutes, contact support.
            </div>
          )}

          <div className="border border-border rounded-lg p-6 text-left bg-card space-y-2 text-sm mb-8">
            <div className="label-caps text-[10px] mb-2">Setup summary</div>
            <Row label="Business name" value={submitted.business_name} />
            <Row label="Admin email" value={submitted.admin_email} />
            {submitted.contact_phone && <Row label="Phone" value={submitted.contact_phone} />}
            <Row
              label="Brand color"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 rounded-sm border border-border" style={{ backgroundColor: submitted.primary_color }} />
                  <code>{submitted.primary_color}</code>
                </span>
              }
            />
            <Row label="Status" value={<span className="capitalize">{submitted.status.replace("_", " ")}</span>} />
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            {p && (
              <Button asChild size="lg" data-testid="whitelabel-visit-btn">
                <a href={p.branded_url} target="_blank" rel="noreferrer">Visit my site <ArrowRight size={14} className="ml-2" /></a>
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/">Back to homepage</Link>
            </Button>
            <Button variant="ghost" onClick={() => setSubmitted(null)} data-testid="whitelabel-onboarding-edit">
              Update my info
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="text-xs text-muted-foreground hidden sm:flex items-center gap-2">
            <ShieldCheck size={14} className="text-primary" /> Signed in as {user.email}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-6 lg:p-12 py-16">
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-medium">
          <Sparkles size={13} /> Step 2 of 2 · Set up your instance
        </div>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">
          Let's brand <span className="italic text-primary">your platform.</span>
        </h1>
        <p className="text-muted-foreground mb-10">
          Fill in a few details and we'll spin up a fully branded copy of the whole platform for you. Takes 24–48 business hours.
        </p>

        <form onSubmit={submit} className="space-y-6" data-testid="whitelabel-onboarding-form">
          <Field label="Your business name *" hint="Displayed on every page: header, emails, certificates, PDFs.">
            <Input
              value={form.business_name}
              onChange={(e) => set("business_name", e.target.value)}
              placeholder="e.g. Skyline Tax & Advisory"
              required
              data-testid="wl-onb-business-name"
            />
          </Field>

          <Field label="Admin email *" hint="This is where we send your admin login.">
            <Input
              type="email"
              value={form.admin_email}
              onChange={(e) => set("admin_email", e.target.value)}
              placeholder="you@yourbusiness.com"
              required
              data-testid="wl-onb-admin-email"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Contact phone" hint="For urgent onboarding questions.">
              <Input
                value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)}
                placeholder="(555) 555-5555"
                data-testid="wl-onb-phone"
              />
            </Field>

            <Field label="Primary brand color" hint="Your buttons, links, and accents.">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  className="h-10 w-14 rounded border border-border cursor-pointer"
                  data-testid="wl-onb-color-picker"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  className="font-mono text-xs"
                  data-testid="wl-onb-color-hex"
                />
              </div>
            </Field>
          </div>

          <Field
            label="Logo URL"
            hint="Direct URL to a PNG/SVG. Skip for now if you don't have one — we'll email you an upload link."
          >
            <Input
              value={form.logo_url}
              onChange={(e) => set("logo_url", e.target.value)}
              placeholder="https://…/logo.png"
              data-testid="wl-onb-logo-url"
            />
          </Field>

          <Field
            label="Preferred subdomain"
            hint={`Your instance will live at yoursubdomain.dynamicfinancialsws.online (bring your own domain later).`}
          >
            <div className="flex items-center gap-2">
              <Input
                value={form.preferred_subdomain}
                onChange={(e) => set("preferred_subdomain", e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))}
                placeholder="skyline-tax"
                data-testid="wl-onb-subdomain"
                className="font-mono"
              />
              <span className="text-sm text-muted-foreground shrink-0">.dynamicfinancialsws.online</span>
            </div>
          </Field>

          <Field label="Anything else we should know?" hint="Optional. Special requests, timing, questions.">
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="e.g. I'd like the courses hidden — I'm only using the client-facing side."
              data-testid="wl-onb-notes"
            />
          </Field>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <Button type="submit" size="lg" disabled={saving} data-testid="wl-onb-submit">
              {saving ? "Submitting…" : "Submit setup info"} <ArrowRight size={14} className="ml-2" />
            </Button>
            <Button type="button" size="lg" variant="outline" asChild>
              <Link to="/white-label">Cancel — back to sales page</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="font-medium text-sm mb-1.5 inline-block">{label}</Label>
    {children}
    {hint && <div className="text-xs text-muted-foreground mt-1.5">{hint}</div>}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-foreground text-right">{value}</span>
  </div>
);
